import { InterceptSendToEndpoint, ProcessorDefinition } from '@kaoto/camel-catalog/types';
import { isDefined } from '@kaoto/forms';

import { getCamelRandomId } from '../../../camel-utils/camel-random-id';
import { CatalogKind } from '../../catalog-kind';
import { EntityType } from '../../entities/base-entity';
import { BaseVisualEntity, IVisualizationNode, IVisualizationNodeData, NodeInteraction } from '../base-visual-entity';
import { NodeIdentity } from '../node-identity';
import { AbstractCamelVisualEntity } from './abstract-camel-visual-entity';
import { NodeEnrichmentService } from './nodes/node-enrichment.service';
import { NodeMapperService } from './nodes/node-mapper.service';
import { ProcessorStepsService } from './support/processor-steps.service';

export class CamelInterceptSendToEndpointVisualEntity
  extends AbstractCamelVisualEntity<{ interceptSendToEndpoint: InterceptSendToEndpoint }>
  implements BaseVisualEntity
{
  id: string;
  interceptSendToEndpointDef: { interceptSendToEndpoint: Exclude<InterceptSendToEndpoint, string> };
  readonly type = EntityType.InterceptSendToEndpoint;
  static readonly ROOT_PATH = 'interceptSendToEndpoint';

  constructor(
    interceptSendToEndpointRaw: { interceptSendToEndpoint: InterceptSendToEndpoint } = { interceptSendToEndpoint: {} },
  ) {
    let interceptSendToEndpointDef: { interceptSendToEndpoint: Exclude<InterceptSendToEndpoint, string> };
    if (typeof interceptSendToEndpointRaw.interceptSendToEndpoint === 'string') {
      interceptSendToEndpointDef = {
        interceptSendToEndpoint: {
          id: getCamelRandomId(CamelInterceptSendToEndpointVisualEntity.ROOT_PATH),
          uri: interceptSendToEndpointRaw.interceptSendToEndpoint,
        },
      };
    } else {
      interceptSendToEndpointDef = { interceptSendToEndpoint: interceptSendToEndpointRaw.interceptSendToEndpoint };
    }

    super(interceptSendToEndpointDef);
    this.interceptSendToEndpointDef = interceptSendToEndpointDef;
    const id =
      interceptSendToEndpointDef.interceptSendToEndpoint.id ??
      getCamelRandomId(CamelInterceptSendToEndpointVisualEntity.ROOT_PATH);
    this.id = id;
    this.interceptSendToEndpointDef.interceptSendToEndpoint.id = id;
  }

  static isApplicable(
    interceptSendToEndpointDef: unknown,
  ): interceptSendToEndpointDef is { interceptSendToEndpoint: InterceptSendToEndpoint } {
    if (
      !isDefined(interceptSendToEndpointDef) ||
      Array.isArray(interceptSendToEndpointDef) ||
      typeof interceptSendToEndpointDef !== 'object'
    ) {
      return false;
    }

    const objectKeys = Object.keys(interceptSendToEndpointDef!);

    return (
      objectKeys.length === 1 &&
      this.ROOT_PATH in interceptSendToEndpointDef! &&
      (typeof interceptSendToEndpointDef.interceptSendToEndpoint === 'object' ||
        typeof interceptSendToEndpointDef.interceptSendToEndpoint === 'string')
    );
  }

  getRootPath(): string {
    return CamelInterceptSendToEndpointVisualEntity.ROOT_PATH;
  }

  getId(): string {
    return this.id;
  }

  setId(id: string): void {
    this.id = id;
    this.interceptSendToEndpointDef.interceptSendToEndpoint.id = id;
  }

  getNodeInteraction(data: IVisualizationNodeData): NodeInteraction {
    const stepsProperties = ProcessorStepsService.getProcessorStepsProperties(
      data.primaryNodeId?.name as keyof ProcessorDefinition,
    );
    const canHavePreviousStep = ProcessorStepsService.canHavePreviousStep(
      data.primaryNodeId?.name as keyof ProcessorDefinition,
    );
    const canHaveChildren = stepsProperties.some((property) => property.type === 'branch');
    const canHaveSpecialChildren = Object.keys(stepsProperties).length > 1;
    const canReplaceStep = data.path !== CamelInterceptSendToEndpointVisualEntity.ROOT_PATH;
    const canRemoveStep = data.path !== CamelInterceptSendToEndpointVisualEntity.ROOT_PATH;
    const canBeDisabled = data.schema?.properties?.disabled !== undefined;

    return {
      canHavePreviousStep,
      canHaveNextStep: canHavePreviousStep,
      canHaveChildren,
      canHaveSpecialChildren,
      canReplaceStep,
      canRemoveStep,
      canRemoveFlow: data.path === CamelInterceptSendToEndpointVisualEntity.ROOT_PATH,
      canBeDisabled,
    };
  }

  async toVizNode(): Promise<IVisualizationNode<IVisualizationNodeData>> {
    const interceptSendToEndpointGroupNode = await NodeMapperService.getVizNode(
      CamelInterceptSendToEndpointVisualEntity.ROOT_PATH,
      {
        primaryNodeId: {
          name: CamelInterceptSendToEndpointVisualEntity.ROOT_PATH as keyof ProcessorDefinition,
          catalogKind: CatalogKind.Entity,
        } satisfies NodeIdentity,
      },
      this.interceptSendToEndpointDef,
    );
    interceptSendToEndpointGroupNode.data.entity = this;
    interceptSendToEndpointGroupNode.data.isGroup = true;
    interceptSendToEndpointGroupNode.data.catalogKind = CatalogKind.Entity;
    interceptSendToEndpointGroupNode.data.name = this.type;
    interceptSendToEndpointGroupNode.data.primaryNodeId = { name: this.type, catalogKind: CatalogKind.Entity };

    await NodeEnrichmentService.enrichVisualizationTree(interceptSendToEndpointGroupNode);

    return interceptSendToEndpointGroupNode;
  }

  toJSON(): { interceptSendToEndpoint: InterceptSendToEndpoint } {
    return { interceptSendToEndpoint: this.interceptSendToEndpointDef.interceptSendToEndpoint };
  }

  protected getRootUri(): string | undefined {
    return undefined;
  }
}
